import { computed, effect, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { __ } from '@shared/functions/object.functions';

/**
 * The themes of the app. `system` follows the `prefers-color-scheme` of the device.
 */
export type AppTheme = 'dark' | 'light' | 'system';

export const APP_THEMES: AppTheme[] = ['dark', 'light', 'system'];

const STORAGE_KEY: string = 'mp-theme';

/**
 * The dark / light mode of the app. The selected theme is stored locally and applied as `data-mp-theme`
 * on the html element, the css tokens (`--mp-*` in main.scss) switch accordingly. Dark is the default,
 * it is the look of the native app.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {

    // -----------------------------------------------------------------------------------------------------
    // @ INSTANCE VARIABLES
    // -----------------------------------------------------------------------------------------------------

    readonly theme: WritableSignal<AppTheme> = signal(ThemeService.readStoredTheme());

    private readonly systemPrefersLight: WritableSignal<boolean> = signal(ThemeService.systemPrefersLight());

    // -----------------------------------------------------------------------------------------------------
    // @ COMPUTED VARIABLES
    // -----------------------------------------------------------------------------------------------------

    /** The theme that is effectively rendered (`system` resolved). */
    readonly effectiveTheme: Signal<'dark' | 'light'> = computed(() => {
        const theme = this.theme();

        if (theme === 'system') {
            return this.systemPrefersLight() ? 'light' : 'dark';
        }

        return theme;
    });

    readonly isLight: Signal<boolean> = computed(() => this.effectiveTheme() === 'light');

    // -----------------------------------------------------------------------------------------------------
    // @ EFFECTS
    // -----------------------------------------------------------------------------------------------------

    private readonly _apply = effect(() => {
        const effective = this.effectiveTheme();

        if (effective === 'light') {
            document.documentElement.setAttribute('data-mp-theme', 'light');
        } else {
            document.documentElement.removeAttribute('data-mp-theme');
        }
    });

    // -----------------------------------------------------------------------------------------------------
    // @ CONSTRUCTOR
    // -----------------------------------------------------------------------------------------------------

    constructor() {
        const media = window.matchMedia?.('(prefers-color-scheme: light)');

        media?.addEventListener?.('change', (event: MediaQueryListEvent) => this.systemPrefersLight.set(event.matches));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC METHODS
    // -----------------------------------------------------------------------------------------------------

    /**
     * Selects a theme and stores it locally.
     *
     * @param theme - The theme to apply.
     * @returns {void}
     */
    setTheme(theme: AppTheme): void {
        this.theme.set(theme);

        try {
            localStorage.setItem(STORAGE_KEY, theme);
        } catch {
            // Storage may be unavailable (private mode), the theme then only lasts for the session
        }
    }

    /** Switches to the next theme (dark → light → system). */
    cycle(): void {
        const index = APP_THEMES.indexOf(this.theme());

        this.setTheme(APP_THEMES[(index + 1) % APP_THEMES.length]);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ PRIVATE STATIC METHODS
    // -----------------------------------------------------------------------------------------------------

    private static readStoredTheme(): AppTheme {
        try {
            const stored = localStorage.getItem(STORAGE_KEY) as AppTheme;

            return APP_THEMES.includes(stored) ? stored : 'dark';
        } catch {
            return 'dark';
        }
    }

    private static systemPrefersLight(): boolean {
        return window.matchMedia?.('(prefers-color-scheme: light)')?.matches === true;
    }
}
